// The local-time interval an event occupies, and which days/ranges it touches.
// Every grid places events through these helpers so week, day and month agree
// on multi-day and cross-midnight events, and the page's "anything in view?"
// check counts exactly what the grids render.

import { addDays, parseISO, startOfDay } from 'date-fns'
import type { CalendarEvent } from './typings'

export type EventInterval = { start: Date; end: Date }

/**
 * `[start, end)` in local time. All-day events always cover at least their start
 * day (a zero-length or same-day all-day event still fills that day); a timed
 * event whose end precedes its start collapses to an instant at its start.
 */
export function eventInterval(event: CalendarEvent): EventInterval {
  const start = parseISO(event.start)
  const end = parseISO(event.end)
  if (event.allDay) {
    const minEnd = addDays(startOfDay(start), 1)
    return { start, end: end < minEnd ? minEnd : end }
  }
  return { start, end: end < start ? start : end }
}

/** Whether the event occupies any part of `[rangeStart, rangeEnd)`. */
export function overlapsRange(
  event: CalendarEvent,
  rangeStart: Date,
  rangeEnd: Date,
): boolean {
  const { start, end } = eventInterval(event)
  if (start >= rangeEnd) return false
  // An instant (zero-length timed event) belongs to the range containing it.
  return end.getTime() === start.getTime()
    ? start >= rangeStart
    : end > rangeStart
}

/** Indexes into `days` (each a local midnight) that the event touches. */
export function daysCovered(event: CalendarEvent, days: Date[]): number[] {
  const indexes: number[] = []
  days.forEach((day, i) => {
    if (overlapsRange(event, day, addDays(day, 1))) indexes.push(i)
  })
  return indexes
}

/** The event's interval clipped to one day — for positioning its slice of a timed column. */
export function clipToDay(event: CalendarEvent, day: Date): EventInterval {
  const { start, end } = eventInterval(event)
  const dayEnd = addDays(day, 1)
  return {
    start: start < day ? day : start,
    end: end > dayEnd ? dayEnd : end,
  }
}
